/**
 * IndexedDB als Ausführung des Offline-Speichers (S4.3) — der browserabhängige
 * Teil. Schnittstelle, flüchtiger Ersatz und Rückfall stehen seit M1.2 in
 * `@shared/mobil/offline/db` und werden hier weiterexportiert.
 *
 * Warum IndexedDB und nicht `localStorage`: dort passen nur Strings, und die
 * Warteschlange trägt Fotos. Ein `Blob` überlebt hier den Neustart des Browsers,
 * ohne vorher in base64 aufgeblasen zu werden.
 *
 * Warum kein `idb` o. Ä.: gebraucht werden vier Operationen auf vier Lagern ohne
 * Indizes, Transaktionen über Lagergrenzen oder Migrationen. Das ist weniger Code
 * als die Einbindung einer Abhängigkeit kostet.
 *
 * Der Wechsel auf den flüchtigen Speicher hängt **nicht** allein daran, ob es
 * `indexedDB` gibt. Der häufigere Fall ist der, in dem es das Objekt gibt und der
 * Zugriff trotzdem scheitert (Safari im privaten Modus, geblockte Website-Daten,
 * voller Datenträger). Ohne `withMemoryFallback` schlüge dort jedes Ablegen fehl,
 * `sync.enqueue` schluckte den Fehler — und das Häkchen des Fahrers verschwände
 * spurlos, ohne dass je eine Anfrage rausginge.
 */
import { createMemoryDb, withMemoryFallback, type OfflineDb, type StoreName } from "@shared/mobil/offline/db";

export { createMemoryDb, withMemoryFallback, type OfflineDb, type StoreName };

const STORE_NAMES: StoreName[] = ["day", "orders", "reference", "outbox"];

const DB_NAME = "cleverfleet-mobil";
const DB_VERSION = 1;

/** `IDBRequest` als Promise. Ohne diesen Wrapper stünde jede Operation unten dreimal als Ereignis-Verdrahtung da. */
function fromRequest<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error("IndexedDB-Anfrage fehlgeschlagen."));
  });
}

function openDatabase(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      // Schlüssel außerhalb des Werts (`put(value, key)`): die Werte sind
      // Serverobjekte, und ihnen ein `keyPath`-Feld anzuhängen hieße, sie beim
      // Lesen wieder davon zu befreien.
      for (const name of STORE_NAMES) {
        if (!request.result.objectStoreNames.contains(name)) request.result.createObjectStore(name);
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error("IndexedDB ließ sich nicht öffnen."));
    // Eine andere Lasche hält eine ältere Fassung offen. Kommt erst bei einer
    // künftigen DB_VERSION vor, bleibt aber ohne Behandlung ein stiller Hänger.
    request.onblocked = () => reject(new Error("Der Gerätespeicher ist von einem anderen Fenster belegt."));
  });
}

export function createIndexedDb(): OfflineDb {
  // Die Verbindung wird einmal geöffnet und geteilt. Beim Fehlschlag wird das
  // Versprechen verworfen, damit der nächste Zugriff es erneut versucht.
  let connection: Promise<IDBDatabase> | null = null;
  const connect = () => {
    if (!connection) {
      connection = openDatabase().catch((error) => {
        connection = null;
        throw error;
      });
    }
    return connection;
  };

  const run = async <T>(store: StoreName, mode: IDBTransactionMode, action: (objectStore: IDBObjectStore) => IDBRequest<T>): Promise<T> => {
    const db = await connect();
    const transaction = db.transaction(store, mode);
    const result = await fromRequest(action(transaction.objectStore(store)));
    // Schreibvorgänge gelten erst mit `complete` als abgelegt: `put` meldet früher
    // Erfolg, als die Transaktion festgeschrieben ist. Für die Warteschlange ist
    // genau das der Unterschied zwischen „liegt auf dem Gerät" und „war da mal".
    if (mode === "readwrite") {
      await new Promise<void>((resolve, reject) => {
        transaction.oncomplete = () => resolve();
        transaction.onabort = () => reject(transaction.error ?? new Error("Schreibvorgang abgebrochen."));
        transaction.onerror = () => reject(transaction.error ?? new Error("Schreibvorgang fehlgeschlagen."));
      });
    }
    return result;
  };

  return {
    async get<T>(store: StoreName, key: string) {
      const value = await run<T | undefined>(store, "readonly", (objectStore) => objectStore.get(key));
      return value === undefined ? null : value;
    },
    async put(store, key, value) {
      await run(store, "readwrite", (objectStore) => objectStore.put(value, key));
    },
    async remove(store, key) {
      await run(store, "readwrite", (objectStore) => objectStore.delete(key));
    },
    all<T>(store: StoreName) {
      return run<T[]>(store, "readonly", (objectStore) => objectStore.getAll() as IDBRequest<T[]>);
    },
    async keys(store) {
      const keys = await run<IDBValidKey[]>(store, "readonly", (objectStore) => objectStore.getAllKeys() as IDBRequest<IDBValidKey[]>);
      return keys.map(String);
    }
  };
}

let shared: OfflineDb | null = null;

/** Der eine Speicher der Anwendung. IndexedDB, solange er trägt; sonst der flüchtige Ersatz. */
export function offlineDb(): OfflineDb {
  if (!shared) shared = typeof indexedDB === "undefined" ? createMemoryDb() : withMemoryFallback(createIndexedDb());
  return shared;
}
